/**
 * @changelog
 *
 * 2026-02-26 (0.1.0) (jhoward)
 *   - Add /register route with dynamic import for Enroll.vue.
 *
 * 2026-02-27 (0.2.0) (jhoward)
 *   - Import and use authRoutes.
 *   - Add beforeEach hook to hydrate app state and enforce authRequired meta.
 *   - Rename Register route to Enroll.
 *
 * 2026-02-28 (0.3.0) (jhoward)
 *   - Import and add offerRoutes and accountRoutes to router configuration.
 *
 * 2026-03-11 (0.4.0) (jhoward)
 *   - Add 'not-found' catch-all route for 404 handling.
 *   - Add 'pageTitle' and 'twoFactorAuthRequired' meta fields to routes.
 *   - Update imports to use new helpers and types.
 *   - Update navigation guard to fetch state using 'peppermillCustomFetchProxy' and set app state accordingly.
 *   - Set page title from route meta on navigation.
 *   - Add smooth scroll to top on navigation.
 *   - BREAKING: Route meta and navigation guard logic changed; may affect consumers relying on previous behavior.
 *
 * 2026-03-13 (0.4.1) (jhoward)
 *   - Assign 'to' route to appStore.currentRoute in router.beforeEach.
 *
 * 2026-03-17 (0.4.2) (jhoward)
 *   - Add adminRoutes to router and protect admin routes with is_admin check.
 *
 * 2026-03-25 (0.5.0) (jhoward)
 *   - Comment out planned routes for /contact and /accessibility.
 *   - Rename privacy-policy route to lowercase.
 *
 * 2026-03-30 (0.5.1) (jhoward)
 *   - Comment out /contact and /accessibility routes.
 *   - Rename privacy-policy route to lowercase.
 *   - Set document.title on route change.
 *
 * 2026-08-27 (0.5.2) (jhoward)
 *   - Cast appStore.state.user to User for the is_admin check; the minimal pre-2FA user shape has
 *     no is_admin field, so this correctly fails closed (routes to 'home') before 2FA completes.
 *
 * 2026-10-09 (0.5.3) (jhoward)
 *   - Scroll to the point where the header has just collapsed (getHeaderCollapsedScroll) on navigation, rather than to
 *     the very top, so the patron is not shown the whole header on every page change.
 *   - Stop treating a throttled (429), failed (5xx) or unreachable /state as "signed out". The guard used to mark the
 *     state loaded with no user and redirect to login, stranding a valid session there until reload. It now tells the
 *     guest, leaves the state unloaded so the next navigation retries, and skips the login redirect for that
 *     navigation.
 *
 * 2026-10-09 (0.6.0) (jhoward)
 *   - Add the public /faq route, open to everyone signed in or not.
 *
 * 2026-10-10 (0.7.0) (jhoward)
 *   - Turn the /contact route back on: the Help panel's hand-off sends guests there instead of showing the contact
 *     form inside the panel. /accessibility stays off.
 *
 * 2026-10-15 (0.7.1) (jhoward)
 *   - Treat a /state call that throws (offline, or an HTML 502 from a proxy) as state unavailable. The framework fetch
 *     rejects rather than answering without a status, so the "unreachable" branch never ran: the guard threw, the
 *     navigation failed, and the first page stayed blank.
 */

import type { FetchResponse, PeppermillServerState } from '@framework/defs/framework';
import { createRouter, createWebHistory } from 'vue-router';
import type { User } from '@/defs/user';
import { getHeaderCollapsedScroll } from '@/helpers/headerMetrics';
import peppermillCustomFetchProxy from '@/helpers/peppermillCustomFetchProxy';
import { syncPushRegistration } from '@/helpers/registerPushNotifications';
import accountRoutes from '@/router/account';
import adminRoutes from '@/router/admin';
import authRoutes from '@/router/auth';
import offerRoutes from '@/router/offers';
import useAppStore from '@/store';
import type { AppServerState } from '@/store';
import Home from '@/views/Home.vue';
import { state } from '@rss/routes/app';

export interface StateResponse extends FetchResponse {
  data: Pick<PeppermillServerState, 'user' | 'csrfToken' | 'honeypot'> & { redirect?: string };
}

const router = createRouter({
  history: createWebHistory(import.meta.env.VITE_ASSET_URL || '/'),
  routes: [
    {
      path: '/',
      name: 'home',
      component: Home,
      meta: {
        pageTitle: 'Home',
        authRequired: true,
        twoFactorAuthRequired: true,
      },
    },

    {
      path: '/contact',
      name: 'contact',
      component: () => import('@/views/Contact.vue'),
      meta: {
        pageTitle: 'Contact Us',
        authRequired: false,
        twoFactorAuthRequired: false,
      },
    },

    // {
    //     path: '/accessibility',
    //     name: 'accessibility',
    //     component: () => import('@/views/Accessibility.vue'),
    //     meta: {
    //       pageTitle: 'Accessibility',
    //       authRequired: false,
    //       twoFactorAuthRequired: false,
    //     }
    // },

    {
      path: '/faq',
      name: 'faq',
      component: () => import('@/views/Faq.vue'),
      meta: {
        pageTitle: 'Frequently Asked Questions',
        authRequired: false,
        twoFactorAuthRequired: false,
      },
    },

    {
      path: '/privacy-policy',
      name: 'privacy-policy',
      component: () => import('@/views/PrivacyPolicy.vue'),
      meta: {
        pageTitle: 'Privacy Policy',
        authRequired: false,
        twoFactorAuthRequired: false,
      },
    },
    ...authRoutes,
    ...offerRoutes,
    ...accountRoutes,
    ...adminRoutes,
    {
      name: 'not-found',
      path: '/:catchAll(.*)',
      component: () => import('@/views/404Error.vue'),
      meta: {
        pageTitle: 'Page Not Found',
        authRequired: false,
        twoFactorAuthRequired: false,
      },
    },
  ],
});

router.beforeEach(async (to, from) => {
  const appStore = useAppStore();
  appStore.currentRoute = to;
  if (!appStore.state.isLoaded) {
    const stateResponse = await peppermillCustomFetchProxy<StateResponse>(
      {},
      {
        route: state(),
      },
    ).catch((error: unknown): StateResponse => {
      //No status reads as unreachable below
      console.error(error);
      return { status: 0 } as StateResponse;
    });
    //A throttled, failed or unreachable /state says nothing about whether the guest is signed in. Reading it as
    //"signed out" sent a valid session to the login page, and marking the state loaded kept it there until reload.
    //Left unloaded, so the next navigation asks again, and the auth redirect below is skipped for this one.
    const isStateUnavailable =
      !stateResponse.data && (!stateResponse.status || 429 === stateResponse.status || stateResponse.status >= 500);

    if (stateResponse.data) {
      appStore.updateStateFromServer(stateResponse.data as PeppermillServerState & AppServerState);
    } else if (isStateUnavailable) {
      appStore.setNewSnack(
        15000,
        429 === stateResponse.status
          ? 'Too many requests. Please wait a minute and try again.'
          : 'We could not reach the server. Please try again shortly.',
        'error',
      );
    } else {
      appStore.state = {
        isLoaded: true,
        user: null,
        twoFactorAuthRequired: true,
        //Part of the state's shape, and it was missing here - so this branch was assigning an object the type
        //does not describe, and every read of it afterwards was reading undefined.
        new_pin_required: false,
        reauthenticationRequired: false,
        reauthenticationCallback: null,
      };
    }
  }

  //Not the very top: the header collapses as the page scrolls, and a patron has no need to see all of it on every
  //navigation, so land where it has just finished collapsing. A page too short to scroll that far stays where it is.
  window.scroll({
    top: getHeaderCollapsedScroll(),
    left: 0,
    behavior: 'smooth',
  });

  if (to.meta?.authRequired && !appStore.state.user && appStore.state.isLoaded) {
    return { name: 'login', query: { redirect: to.fullPath } };
  }

  if (to.meta?.adminRequired && !(appStore.state.user as User | null)?.is_admin) {
    return { name: 'home' };
  }

  //Fire and forget: re-asserts an already-granted device token so one OPC Admin pruned in error comes back. Never
  //awaited, so it cannot slow a navigation, and it never prompts - see syncPushRegistration().
  if (appStore.state.user && !appStore.state.twoFactorAuthRequired) {
    void syncPushRegistration();
  }

  document.title = appStore.pageTitle = <string>to.meta.pageTitle || appStore.appTitle;

  return true;
});

export default router;
